import { useEffect, useState } from 'react';
import { adminApi } from '../../api/endpoints.js';
import { useNotice } from '../../state/NoticeContext.jsx';
import { Banner, Button, Card, Muted, Spinner, Tag, Text, fmtDate, useAsync } from '../../ui.jsx';

export default function AdminDates() {
  const notice = useNotice();
  const current = useAsync(() => adminApi.dates(), []);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const dates = current.data?.dates;
  useEffect(() => {
    if (!dates || form) return;
    setForm({
      deadline: dates.deadline ? String(dates.deadline).slice(0, 10) : '',
      reportingDate: dates.reportingDate ? String(dates.reportingDate).slice(0, 10) : '',
      deadlineNote: dates.deadlineNote || '',
      admissionsOpen: Boolean(dates.admissionsOpen)
    });
  }, [dates]);

  if (current.loading || !form) return <Spinner label="Loading your dates" />;

  const set = (patch) => setForm({ ...form, ...patch });

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const data = await adminApi.saveDates({
        deadline: form.deadline || null,
        reportingDate: form.reportingDate || null,
        deadlineNote: form.deadlineNote,
        admissionsOpen: form.admissionsOpen
      });
      current.setData({ dates: data.dates });
      notice.success('Dates published. Students now see these on your school page.');
    } catch (error) {
      notice.fromError(error);
    } finally {
      setBusy(false);
    }
  };

  /** Clears the deadline so students are told the date is not published yet. */
  const clearDeadline = async () => {
    setBusy(true);
    try {
      const data = await adminApi.saveDates({ deadline: null });
      current.setData({ dates: data.dates });
      setForm({ ...form, deadline: '' });
      notice.success('Deadline cleared.');
    } catch (error) {
      notice.fromError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h2>Application dates</h2>
      <Muted>
        These are your dates. Entrance never sets them for you, and re-seeding reference data cannot
        change them.
      </Muted>

      <Card>
        <div className="row sp">
          <b>What students see right now</b>
          <Tag tone={dates.isClosed ? 'no' : form.admissionsOpen ? 'ok' : 'mid'}>
            {dates.statusLabel}
          </Tag>
        </div>
        <p className="mu" style={{ marginTop: 8 }}>
          Deadline {fmtDate(dates.deadline)}. Reporting date {fmtDate(dates.reportingDate)}.
          {dates.deadlineNote ? ` ${dates.deadlineNote}` : ''}
        </p>
      </Card>

      <form onSubmit={save}>
        <Card>
          <Text
            label="Application deadline"
            type="date"
            hint="Leave empty if you have not published a date. Students then see that the date is not yet published."
            value={form.deadline}
            onChange={(e) => set({ deadline: e.target.value })}
          />
          <div style={{ height: 12 }} />
          <Text
            label="Results reporting date"
            type="date"
            hint="When results come out. It must be on or after the deadline."
            value={form.reportingDate}
            onChange={(e) => set({ reportingDate: e.target.value })}
          />
          <div style={{ height: 12 }} />
          <Text
            label="Note for students"
            hint="For example: 30 Sep 2026 is the last day to buy an undergraduate e-voucher."
            value={form.deadlineNote}
            maxLength={500}
            onChange={(e) => set({ deadlineNote: e.target.value })}
          />
        </Card>

        <Card>
          <label className="row sp" style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.admissionsOpen}
              onChange={(e) => set({ admissionsOpen: e.target.checked })}
              style={{ width: 'auto', margin: 0 }}
            />
            <div>
              <b>Accepting applications</b>
              <div className="mu">
                Turn this off to stop new applications while you prepare, without publishing a deadline.
              </div>
            </div>
          </label>
        </Card>

        <div className="row">
          <Button type="submit" busy={busy}>Publish dates</Button>
          {form.deadline ? (
            <Button type="button" variant="s" busy={busy} onClick={clearDeadline}>
              Clear the deadline
            </Button>
          ) : null}
        </div>
      </form>

      {!form.admissionsOpen ? (
        <Banner title="Applications are paused">
          Turn "Accepting applications" back on to let students start an application.
        </Banner>
      ) : null}
    </>
  );
}
